import { useMemo } from 'react';
import {
  LayoutValuationResponse,
  AbsorptionAndLiquidityResponse,
  LayoutValuationPayload,
  InstallmentsSimulationResponse,
  InstallmentsSimulation,
  ILayoutValuation,
  PolygonType,
  AbsorptionAndLiquidityData,
} from '../../../redux/studies.model';
import {
  labelFormatterMoneyAsInteger,
  labelFormatterPercentile,
  labelFormatterPercentileInteger,
} from '../../../../../../shared/formatters/formatters';
import { mapStaticFactory } from '../../../../../../shared/utils/map.static.factory';
import {
  FormattedPDFData,
  HeaderPDFData,
  LayoutsFormattedPDFData,
} from './types';

interface UsePDFDataParams {
  polygon: PolygonType;
  expandedArea?: google.maps.Circle | null;
  layoutCharacteristics: LayoutValuationPayload | null;
  layoutValuationResponsePrimary: LayoutValuationResponse | null;
  layoutValuationResponseSecondary: LayoutValuationResponse | null;
  absorptionAndLiquidity: AbsorptionAndLiquidityResponse | null;
  installmentsSimulationPrimary: InstallmentsSimulationResponse | null;
  installmentsSimulationSecondary: InstallmentsSimulationResponse | null;
}

export const usePDFData = ({
  polygon,
  expandedArea,
  layoutValuationResponsePrimary,
  layoutValuationResponseSecondary,
  absorptionAndLiquidity,
  installmentsSimulationPrimary,
  installmentsSimulationSecondary,
  layoutCharacteristics,
}: UsePDFDataParams): LayoutsFormattedPDFData => {
  const imgURLs = {
    polygon: polygon
      ? mapStaticFactory({
          polygon,
          height: 274,
          width: 547,
          scale: 2,
          fillColor: '0x9f3eaf',
          color: '0x925AC4',
          strokeWidth: 2,
        })
      : null,
    expandedArea: expandedArea
      ? mapStaticFactory({
          polygon: expandedArea,
          height: 274,
          width: 547,
          scale: 2,
          fillColor: '0x9f3eaf',
          color: '0x925AC4',
          strokeWidth: 2,
        })
      : null,
  };
  const layoutsFormattedData: FormattedPDFData[] | null = useMemo(() => {
    if (layoutCharacteristics) {
      return layoutCharacteristics.map((data, _index) => {
        const { realtyType, area, bedrooms, suites, garages, id } = data;
        const header: HeaderPDFData = {
          layoutNumber: id,
          characteristics: {
            realtyType,
            area,
            bedrooms,
            suites,
            garages,
          },
        };
        let primaryMarketData;
        let secondaryMarketData;
        if (layoutValuationResponsePrimary) {
          const currentLayoutIndex = layoutValuationResponsePrimary.findIndex(
            (layout) => layout.id === id
          );

          primaryMarketData = getMarketData(
            layoutValuationResponsePrimary[currentLayoutIndex],
            installmentsSimulationPrimary?.[currentLayoutIndex],
            'primaryMarketVGV',
            layoutValuationResponsePrimary[currentLayoutIndex].vgv
              ?.numberOfUnits
          );
          if (layoutValuationResponseSecondary) {
            secondaryMarketData = getMarketData(
              layoutValuationResponseSecondary[currentLayoutIndex],
              installmentsSimulationSecondary?.[currentLayoutIndex],
              'secondaryMarketVGV',
              layoutValuationResponsePrimary[currentLayoutIndex].vgv
                ?.numberOfUnits
            );
          }
        }
        let absorptionAndLiquidityData;
        if (absorptionAndLiquidity) {
          const currentAbsIndex = absorptionAndLiquidity.findIndex(
            (abs) => abs.id === id
          );
          const { absorption, qualifiedAudience, liquidity, conversionRate } =
            absorptionAndLiquidity[currentAbsIndex];
          absorptionAndLiquidityData = {
            unitTotalValue: labelFormatterMoneyAsInteger(
              installmentsSimulationPrimary?.[currentAbsIndex].unitTotalValue
                ? installmentsSimulationPrimary?.[currentAbsIndex]
                    .unitTotalValue
                : 0
            ),
            absorption,
            qualifiedAudience,
            liquidity,
            conversionRate,
          };
        }
        const currentLayout: AbsorptionAndLiquidityData | undefined =
          absorptionAndLiquidity?.find((abs) => abs.id === id);

        return {
          header,
          primaryMarketData,
          secondaryMarketData,
          absorptionAndLiquidityData,
          phasingEstimate: currentLayout?.phasingEstimate,
        };
      });
    } else {
      return null;
    }
  }, [
    layoutValuationResponsePrimary,
    layoutValuationResponseSecondary,
    absorptionAndLiquidity,
    installmentsSimulationPrimary,
    installmentsSimulationSecondary,
    layoutCharacteristics,
  ]);
  return {
    imgURLs,
    layoutsFormattedData,
  };
};

const getMarketData = (
  layoutData: ILayoutValuation,
  installmentsData: InstallmentsSimulation | undefined,
  VGVKey: 'primaryMarketVGV' | 'secondaryMarketVGV',
  numberOfUnits: number | undefined
) => {
  const { pricePerSqMt, pricePerUnit, vgv } = layoutData;
  const valuationVGV = {
    pricePerUnit: labelFormatterMoneyAsInteger(pricePerUnit),
    pricePerSqMt: labelFormatterMoneyAsInteger(pricePerSqMt) as string,
    numberOfUnits,
    isEdited: layoutData.isEdited,
    vgv: vgv?.[VGVKey]
      ? labelFormatterMoneyAsInteger(vgv?.[VGVKey] as number)
      : 'R$ 0',
  };
  let installmentsSimulation;
  if (installmentsData) {
    const {
      downPaymentPercentile,
      numberOfInstallments,
      monthlyInterestPercentile,
      installmentValue,
      unitTotalValue,
      interestValue,
    } = installmentsData;
    installmentsSimulation = {
      downPaymentPercentile: labelFormatterPercentileInteger(
        Number(downPaymentPercentile)
      ),
      numberOfInstallments,
      monthlyInterestPercentile: labelFormatterPercentile(
        Number(monthlyInterestPercentile)
      ),
      installmentValue: labelFormatterMoneyAsInteger(
        installmentValue ? installmentValue : 0
      ),
      unitTotalValue: labelFormatterMoneyAsInteger(unitTotalValue),
      interestValue: labelFormatterMoneyAsInteger(interestValue),
    };
  }
  const data = { valuationVGV, installmentsSimulation };
  return data;
};
